import { format } from 'date-fns';

import { defaultDateFormat } from '@epi-constants/dateFormats';
import { ApiError } from '@epi-helpers/ApiError';
import {
  ValidateUserDataError,
  ValidateUserDataResponse
} from '@epi-models/pages/MassUpload';

export const getImportCellValue = (cell: unknown): unknown => {
  if (!cell || typeof cell !== 'object') {
    return cell;
  }

  if ('value' in (cell as Record<string, unknown>)) {
    return (cell as { value?: unknown }).value;
  }

  return cell;
};

export const isEmptyImportCellValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

const toImportPrimitiveString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

export const getImportCellStringValue = (cell: unknown): string =>
  toImportPrimitiveString(getImportCellValue(cell));

export const getImportCellNullableStringValue = (
  cell: unknown
): string | null => {
  const value = getImportCellStringValue(cell);

  return value === '' ? null : value;
};

export const resolveBenefitsStartDate = (value: unknown): string => {
  const dateValue = toImportPrimitiveString(value);

  return dateValue === '' ? format(new Date(), defaultDateFormat) : dateValue;
};

export const resolveBenefitGroup = (
  value: unknown,
  defaultGroup?: string | number
): string | number | undefined =>
  isEmptyImportCellValue(value) ? defaultGroup : (value as string | number);

export const hasNonAutoFillRowData = (
  row: Record<string, unknown>,
  defaultBenefitGroup?: string | number
): boolean =>
  Object.entries(row).some(([columnKey, cell]) => {
    if (columnKey === 'row_number') {
      return false;
    }

    const cellValue = getImportCellValue(cell);
    if (isEmptyImportCellValue(cellValue)) {
      return false;
    }

    if (
      columnKey === 'benefit_group' &&
      defaultBenefitGroup !== undefined &&
      String(cellValue) === String(defaultBenefitGroup)
    ) {
      return false;
    }

    return true;
  });

export const hasAnyUserProvidedImportData = (
  rows: unknown[],
  defaultBenefitGroup?: string | number
): boolean =>
  rows.some(
    row =>
      row &&
      typeof row === 'object' &&
      hasNonAutoFillRowData(row as Record<string, unknown>, defaultBenefitGroup)
  );

const withReviewCellValue = <T>(
  cell: { value?: T } | undefined,
  value: T | undefined
) => ({
  ...cell,
  value
});

export const applyReviewRowDefaults = (
  row: Record<string, unknown>,
  rowIndex: number,
  defaultBenefitGroup?: string | number
): Record<string, unknown> => ({
  ...row,
  row_number: { value: rowIndex + 1 },
  benefits_start_date: withReviewCellValue(
    row.benefits_start_date as { value?: string } | undefined,
    resolveBenefitsStartDate(getImportCellValue(row.benefits_start_date))
  ),
  benefit_group: withReviewCellValue(
    row.benefit_group as { value?: string | number } | undefined,
    resolveBenefitGroup(
      getImportCellValue(row.benefit_group),
      defaultBenefitGroup
    )
  )
});

export const applyResultRowDefaults = (
  row: Record<string, unknown>,
  rowIndex: number,
  defaultBenefitGroup?: string | number
) => ({
  ...row,
  benefits_start_date: resolveBenefitsStartDate(row.benefits_start_date),
  benefit_group: resolveBenefitGroup(row.benefit_group, defaultBenefitGroup),
  row_number: rowIndex + 1
});

export const applyMappedRowBenefitGroup = (
  row: Record<string, unknown>,
  defaultBenefitGroup?: string | number
): Record<string, unknown> => {
  if (
    defaultBenefitGroup === undefined ||
    !hasNonAutoFillRowData(row, defaultBenefitGroup)
  ) {
    return row;
  }

  return {
    ...row,
    benefit_group: resolveBenefitGroup(row.benefit_group, defaultBenefitGroup)
  };
};

type ImportEntryChangeRow = {
  actionType?: string;
  rowIndex: number;
  data: Record<string, unknown>;
};

export const getBenefitGroupPreselectionUpdates = (
  rows: ImportEntryChangeRow[],
  defaultBenefitGroup?: string | number
) => {
  if (defaultBenefitGroup === undefined) {
    return;
  }

  return rows
    .filter(
      row =>
        !resolveBenefitGroup(row.data.benefit_group) &&
        (row.actionType === 'create' ||
          (row.actionType === 'edit' &&
            hasNonAutoFillRowData(row.data, defaultBenefitGroup)))
    )
    .map(row => ({
      rowIndex: row.rowIndex,
      data: { benefit_group: { value: defaultBenefitGroup } }
    }));
};

const FIELD_NAME_PATTERN = /^\[(\d+)]\.(.+)$/;
const WORKING_HOURS_COLUMN_KEY = 'working_hours_percentage';
const LUNCH_BENEFIT_MONTHLY_DAYS_COLUMN_KEY = 'lunch_benefit_monthly_days';

const parseFieldName = (
  fieldName: string
): { rowIndex: number; columnKey: string } | null => {
  const m = FIELD_NAME_PATTERN.exec(fieldName.trim());
  if (!m) {
    return null;
  }
  return { rowIndex: Number(m[1]), columnKey: m[2] };
};

export const getValidationErrorsFromUnknown = (
  validationResult: unknown
): ValidateUserDataError[] => {
  if (!validationResult || typeof validationResult !== 'object') {
    return [];
  }
  if (validationResult instanceof ApiError) {
    const body = validationResult.prop as { errors?: ValidateUserDataError[] };
    if (Array.isArray(body?.errors)) {
      return body.errors;
    }
  }
  const r = validationResult as Record<string, unknown>;
  if (Array.isArray(r.errors)) {
    return r.errors as ValidateUserDataError[];
  }
  const prop = r.prop as Record<string, unknown> | undefined;
  if (prop && Array.isArray(prop.errors)) {
    return prop.errors as ValidateUserDataError[];
  }
  const data = r.data as Record<string, unknown> | undefined;
  if (data && Array.isArray(data.errors)) {
    return data.errors as ValidateUserDataError[];
  }
  return [];
};

export const getValidationErrorsFromResponse = (
  validationResponse: ValidateUserDataResponse
): ValidateUserDataError[] => {
  const invalidItems = validationResponse.responseItems.filter(
    responseItem => !responseItem.isValid
  );

  return invalidItems.flatMap(responseItem =>
    responseItem.errors.map(error => {
      return {
        severity: 'error',
        fieldName: error.fieldName,
        message: error.errorCodes.join(','),
        stackTrace: '',
        code: error.errorCodes[0] ?? 'validation_error',
        elementIndex: Number.isFinite(responseItem.row_number)
          ? responseItem.row_number - 1
          : undefined
      };
    })
  );
};

export const applyCellErrorsFromIssues = (
  data: Record<string, unknown>[],
  issues: ValidateUserDataError[],
  cellErrorMessage: string,
  getErrorMessage?: (columnKey: string, code: string) => string | undefined
): Record<string, unknown>[] => {
  const next = data.map(row => ({ ...row }));

  for (const issue of issues) {
    const parsed = parseFieldName(issue.fieldName);
    const rowIndex =
      parsed?.rowIndex ??
      (typeof issue.elementIndex === 'number' ? issue.elementIndex : null);
    const columnKey =
      parsed?.columnKey ?? (parsed ? undefined : issue.fieldName?.trim());

    if (
      rowIndex == null ||
      !Number.isFinite(rowIndex) ||
      !columnKey ||
      rowIndex < 0 ||
      rowIndex >= next.length
    ) {
      continue;
    }

    const row = next[rowIndex] as Record<string, unknown>;
    const cell = row[columnKey] as Record<string, unknown> | undefined;
    if (!cell) {
      continue;
    }

    const message =
      (issue.code && getErrorMessage?.(columnKey, issue.code)) ||
      cellErrorMessage;

    const existingInfo = Array.isArray(cell.info) ? cell.info : [];
    next[rowIndex] = {
      ...row,
      [columnKey]: {
        ...cell,
        info: [
          ...existingInfo.filter(
            (i: { serverValidation?: boolean }) => !i?.serverValidation
          ),
          {
            level: 'error' as const,
            message,
            serverValidation: true
          }
        ]
      }
    };
  }

  return next;
};

export const getLocalIssues = (
  rows: Record<string, unknown>[],
  invalidCellLabel: string
): ValidateUserDataError[] =>
  rows.reduce((acc, row, rowIndex) => {
    const workingHoursValue = (
      row[WORKING_HOURS_COLUMN_KEY] as { value?: unknown }
    )?.value;

    if (
      workingHoursValue !== undefined &&
      workingHoursValue !== null &&
      workingHoursValue !== ''
    ) {
      const numericValue = Number(workingHoursValue);
      const isInvalidRange =
        !Number.isInteger(numericValue) ||
        numericValue < 1 ||
        numericValue > 100;

      if (isInvalidRange) {
        acc.push({
          severity: 'error',
          fieldName: `[${rowIndex}].${WORKING_HOURS_COLUMN_KEY}`,
          message: invalidCellLabel,
          stackTrace: '',
          code: 'working_hours_out_of_range'
        });
      }
    }

    const lunchBenefitMonthlyDaysValue = (
      row[LUNCH_BENEFIT_MONTHLY_DAYS_COLUMN_KEY] as { value?: unknown }
    )?.value;

    if (
      lunchBenefitMonthlyDaysValue !== undefined &&
      lunchBenefitMonthlyDaysValue !== null &&
      lunchBenefitMonthlyDaysValue !== ''
    ) {
      const lunchBenefitMonthlyDaysNumericValue = Number(
        lunchBenefitMonthlyDaysValue
      );
      const isLunchBenefitMonthlyDaysInvalidRange =
        !Number.isInteger(lunchBenefitMonthlyDaysNumericValue) ||
        lunchBenefitMonthlyDaysNumericValue < 1 ||
        lunchBenefitMonthlyDaysNumericValue > 23;

      if (isLunchBenefitMonthlyDaysInvalidRange) {
        acc.push({
          severity: 'error',
          fieldName: `[${rowIndex}].${LUNCH_BENEFIT_MONTHLY_DAYS_COLUMN_KEY}`,
          message: invalidCellLabel,
          stackTrace: '',
          code: 'lunch_benefit_monthly_days_out_of_range'
        });
      }
    }

    return acc;
  }, [] as ValidateUserDataError[]);
